import React from "react"
import { useNavigate } from "react-router-dom"
import { ApiariesIcon } from "@/assets/icons/bees/apiaries-icon.tsx"
import CloseCrossIcon from "@/assets/icons/misc/close-cross-icon.tsx"
import CircleTickIcon from "@/assets/icons/task/circle-tick-icon.tsx"
import { type RenderFunctionDrawerItemComponentProps } from "@/components/render-function-drawer/render-function-drawer.tsx"
import { useGetApiaryByIdQuery } from "@/features/bees/api/apiaries-api.ts"
import AssociatedList from "@/features/tasks/components/associated-list/associated-list.tsx"
import { cn } from "@/utils/styles.ts"

export const ApiaryCard: React.FC<RenderFunctionDrawerItemComponentProps> = ({
  id,
  isSelected,
  onSelect,
  onDelete,
}) => {
  const { data: workingApiary } = useGetApiaryByIdQuery(id)
  const navigate = useNavigate()

  const navigateToApiary = () => {
    navigate(`/apiaries/${id}/`)
  }

  const handleApiaryClick = () => {
    onSelect && onSelect()
    !onSelect && navigateToApiary()
  }

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation()
    onDelete && onDelete(id)
  }

  return (
    <div
      onClick={handleApiaryClick}
      className={cn(
        "flex items-center justify-between gap-2 rounded-[10px] border border-gray-200 bg-[var(--light-grey)] p-2 text-neutral-700",
        isSelected && "border border-brown-500 bg-white",
      )}
    >
      <div
        className={cn(
          "grid w-[70%] grid-cols-[30px_auto] items-center gap-2 truncate",
          onSelect && "w-full grid-cols-[30px_30px_auto]",
        )}
      >
        {onSelect && (
          <>
            {isSelected ? (
              <CircleTickIcon />
            ) : (
              <div
                className={
                  "border-dark-grey box-border h-[24px] w-[24px] rounded-full border bg-[var(--background-color)] pr-3"
                }
              />
            )}
          </>
        )}
        <div
          className={cn(
            "grid h-[30px] w-[30px] place-content-center rounded-[4px] bg-gray-600 text-white opacity-60",
            isSelected && "bg-brown-500",
          )}
        >
          <ApiariesIcon />
        </div>
        <div className={"flex w-[70%] flex-col"}>
          <div className={"text-[14px]"}>{workingApiary?.name}</div>
          <div className={"truncate text-neutral-700"}>
            {workingApiary?.address}
          </div>
        </div>
      </div>
      {onDelete && (
        <div onClick={handleDelete}>
          <CloseCrossIcon className={"text-neutral-500"} />
        </div>
      )}
    </div>
  )
}

interface AssociatedApiariesListProps {
  apiaries: string[]
  onApiariesChange: (apiaryIds: string[]) => void
}

export const AssociatedApiariesList: React.FC<AssociatedApiariesListProps> = ({
  apiaries,
  onApiariesChange,
}) => {
  return (
    <AssociatedList
      hideMoreText={"hideOtherApiariesAssociated"}
      items={apiaries}
      onItemsChange={onApiariesChange}
      renderItem={(apiary, onDelete) => (
        <ApiaryCard id={apiary} onDelete={onDelete} key={apiary} />
      )}
      seeMoreText={"seeOtherApiariesAssociated"}
    />
  )
}
